import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';


const RiskLevelModal = ({ tenant, isOpen, onClose, onSave }) => {
  const [selectedRiskLevel, setSelectedRiskLevel] = useState(tenant?.riskLevel || 'low');
  const [notes, setNotes] = useState('');

  const riskLevelOptions = [
    { 
      value: 'low', 
      label: 'Low Risk',
      description: 'Tenant is performing well with no concerns'
    },
    { 
      value: 'medium', 
      label: 'Medium Risk',
      description: 'Some concerns but manageable'
    },
    { 
      value: 'high', 
      label: 'High Risk',
      description: 'Requires immediate attention'
    }
  ];

  const getRiskLevelColor = (level) => {
    switch (level) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSave({
      ...tenant,
      riskLevel: selectedRiskLevel,
      riskNotes: notes
    });
    onClose();
  };

  if (!isOpen || !tenant) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Set Risk Level
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
          />
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Tenant Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-blue-600">
                  {tenant?.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900">{tenant?.name}</h3>
                <p className="text-sm text-gray-500">{tenant?.company}</p>
              </div>
            </div>
          </div>

          {/* Current Risk Level */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Current Risk Level
            </label>
            <div className={`inline-flex px-3 py-1 rounded-full text-sm font-medium border ${getRiskLevelColor(tenant?.riskLevel)}`}>
              {tenant?.riskLevel?.charAt(0)?.toUpperCase() + tenant?.riskLevel?.slice(1)} Risk
            </div>
          </div>

          {/* New Risk Level */}
          <Select
            label="New Risk Level"
            options={riskLevelOptions}
            value={selectedRiskLevel}
            onChange={setSelectedRiskLevel}
            required
          />

          {/* Preview */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Preview
            </label>
            <div className={`inline-flex px-3 py-1 rounded-full text-sm font-medium border ${getRiskLevelColor(selectedRiskLevel)}`}>
              {selectedRiskLevel?.charAt(0)?.toUpperCase() + selectedRiskLevel?.slice(1)} Risk
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e?.target?.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Reason for risk level change..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="AlertCircle"
              iconPosition="left"
            >
              Update Risk Level
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RiskLevelModal;